// @spec SHL-STR-018
'use client';

import { CalendarIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { DateRangeFilter } from '@/api/search';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface DateRangeDropdownProps {
  /** Current date range value */
  value: DateRangeFilter | null;
  /** Called when date range changes */
  onChange: (value: DateRangeFilter | null) => void;
}

/** Date range option configuration */
interface DateRangeOption {
  value: DateRangeFilter | null;
  label: string;
}

// Labels are translation keys, resolved at render time
const DATE_RANGE_KEYS: { value: DateRangeFilter | null; labelKey: string }[] = [
  { value: null, labelKey: 'search.anyTime' },
  { value: 'week', labelKey: 'search.pastWeek' },
  { value: 'month', labelKey: 'search.pastMonth' },
  { value: 'year', labelKey: 'search.pastYear' },
];

/**
 * Dropdown for selecting date range filter.
 *
 * Displays a dropdown with date range options.
 * "Any time" clears the filter (null value).
 *
 * @example
 * <DateRangeDropdown
 *   value={dateRange}
 *   onChange={setDateRange}
 * />
 */
export function DateRangeDropdown({ value, onChange }: DateRangeDropdownProps) {
  const { t } = useTranslation('common');
  const dateRangeOptions: DateRangeOption[] = DATE_RANGE_KEYS.map((opt) => ({
    value: opt.value,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
    label: t(opt.labelKey as any),
  }));
  const selectedOption = dateRangeOptions.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label ?? t('search.anyTime');
  const hasValue = value !== null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            hasValue
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          )}
        >
          <CalendarIcon className="h-3 w-3" />
          <span>{displayLabel}</span>
          <ChevronDownIcon className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[140px]">
        {dateRangeOptions.map((option) => (
          <DropdownMenuItem
            key={option.value ?? 'any'}
            onClick={() => {
              onChange(option.value);
            }}
            className="flex items-center justify-between"
          >
            <span>{option.label}</span>
            {value === option.value && <CheckIcon className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
